import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import api from '@/api'
import type { BetBuilderMatch, BetBuilderMatchResponse, BetBuilderPhase } from '@/types/betBuilder'

export const useBetBuilderStore = defineStore('betBuilder', () => {
  const phase = ref<BetBuilderPhase>('idle')
  const targetWin = ref<number>(1000)
  const stake = ref<number>(10)
  const match = ref<BetBuilderMatch | null>(null)
  const error = ref<string | null>(null)
  const loading = ref(false)
  const scanProgress = ref(0)
  const sessionAddedCount = ref(0)
  const buildAttempt = ref(0)
  const usedSelectionKeys = ref<string[]>([])

  const hasResult = computed(() => phase.value === 'result' && match.value !== null)

  function isModuleEnabled(enabledModules: string[] | undefined): boolean {
    return Array.isArray(enabledModules) && enabledModules.includes('bet_builder')
  }

  function reset() {
    phase.value = 'idle'
    match.value = null
    error.value = null
    loading.value = false
    scanProgress.value = 0
    sessionAddedCount.value = 0
    buildAttempt.value = 0
    usedSelectionKeys.value = []
  }

  function returnToConfig() {
    match.value = null
    error.value = null
    scanProgress.value = 0
    loading.value = false
    phase.value = 'config'
  }

  function recordAdded() {
    sessionAddedCount.value += 1
  }

  function openConfig(defaultStake?: number, defaultTarget?: number) {
    if (typeof defaultStake === 'number' && defaultStake > 0) {
      stake.value = defaultStake
    }
    if (typeof defaultTarget === 'number' && defaultTarget > 0) {
      targetWin.value = defaultTarget
    }
    phase.value = 'config'
    match.value = null
    error.value = null
  }

  function close() {
    reset()
  }

  async function findMatch() {
    error.value = null
    match.value = null
    loading.value = true
    phase.value = 'scanning'
    scanProgress.value = 0

    const progressTimer = window.setInterval(() => {
      if (scanProgress.value < 90) {
        scanProgress.value += 8
      }
    }, 120)

    try {
      const { data } = await api.post<{
        status?: string
        result?: BetBuilderMatchResponse
        messages?: string[]
      }>('/bet-builder/match', {
        target_win: targetWin.value,
        stake: stake.value,
        build_index: buildAttempt.value,
        exclude_keys: usedSelectionKeys.value,
      })

      scanProgress.value = 100

      if (data?.status !== 'success' || !data.result?.match) {
        const msg = Array.isArray(data?.messages) ? data.messages[0] : undefined
        throw new Error(msg || 'No match returned from server.')
      }

      match.value = data.result.match
      if (data.result.match.selection_key) {
        const key = data.result.match.selection_key
        if (!usedSelectionKeys.value.includes(key)) {
          usedSelectionKeys.value.push(key)
        }
      }
      buildAttempt.value += 1
      phase.value = 'result'
    } catch (err: unknown) {
      error.value =
        (err as { userMessage?: string; message?: string })?.userMessage ||
        (err as { message?: string })?.message ||
        'Failed to find a matching bet.'
      phase.value = 'config'
    } finally {
      window.clearInterval(progressTimer)
      loading.value = false
    }
  }

  return {
    phase,
    targetWin,
    stake,
    match,
    error,
    loading,
    scanProgress,
    sessionAddedCount,
    buildAttempt,
    usedSelectionKeys,
    hasResult,
    isModuleEnabled,
    reset,
    returnToConfig,
    recordAdded,
    openConfig,
    close,
    findMatch,
  }
})
